import { merge } from 'lodash';

import { Injectable } from '@angular/core';

import { SnFormFieldConfig } from '@lib/form';

@Injectable({ providedIn: 'root' })
export class CameraDetectionsFieldsGeneratorService {
  public genFields(ruleType: string): SnFormFieldConfig[] {
    switch (ruleType) {
      case 'vca_loitering_detection':
        return [
          {
            templateOptions: {
              label: 'Triggers',
            },
            wrappers: ['form-field-group'],
            fieldGroupClassName: 'camera-detections-field-row',
            fieldGroup: [
              this._getTriggerName({
                className: 'camera-detections-field-col-12',
                templateOptions: {
                  required: true,
                },
              }),
              this._getLoiteringTriggerCondition({
                className: 'camera-detections-field-col-12',
                defaultValue: 5,
                templateOptions: {
                  required: true,
                },
              }),
            ],
          },
        ];
      case 'vca_crowd_detection':
        return [
          {
            templateOptions: {
              label: 'Triggers',
            },
            wrappers: ['form-field-group'],
            fieldGroupClassName: 'camera-detections-field-row',
            fieldGroup: [
              this._getTriggerName({
                className: 'camera-detections-field-col-12',
                templateOptions: {
                  required: true,
                },
              }),
              this._getCrowdTriggerCondition({
                className: 'camera-detections-field-col-12',
                defaultValue: 5,
                templateOptions: {
                  required: true,
                },
              }),
            ],
          },
          {
            templateOptions: {
              label: 'Advance',
            },
            wrappers: ['form-field-group'],
            fieldGroupClassName: 'camera-detections-field-row',
            fieldGroup: [
              this._getEnteredZone({
                className: 'camera-detections-field-col-12',
                defaultValue: 2,
                templateOptions: {
                  required: true,
                },
              }),
              this._getLeftZone({
                className: 'camera-detections-field-col-12',
                defaultValue: 2,
                templateOptions: {
                  required: true,
                },
              }),
            ],
          },
        ];
      case 'vca_smart_motion_detection':
        return [
          {
            templateOptions: {
              label: 'Schedule',
            },
            wrappers: ['form-field-group'],
            fieldGroupClassName: 'camera-detections-field-row',
            fieldGroup: [
              this._getSmartMotionDetectionScheduleStart({
                className: 'camera-detections-field-col-6',
                templateOptions: {
                  required: true,
                },
              }),
              this._getSmartMotionDetectionScheduleEnd({
                className: 'camera-detections-field-col-6',
                templateOptions: {
                  required: true,
                },
              }),
            ],
          },
          {
            templateOptions: {
              label: 'Detections',
            },
            wrappers: ['form-field-group'],
            fieldGroupClassName: 'camera-detections-field-row',
            fieldGroup: [
              this._getSmartMotionPeopleDetections({
                className: 'camera-detections-field-col-6',
                templateOptions: {
                  required: true,
                },
              }),
              this._getSmartMotionVehicleDetections({
                className: 'camera-detections-field-col-6',
                templateOptions: {
                  required: true,
                },
              }),
            ],
          },
          {
            templateOptions: {
              label: 'Motion Activity',
            },
            wrappers: ['form-field-group'],
            fieldGroupClassName: 'camera-detections-field-row',
            fieldGroup: [
              this._getSmartMotionActivity({
                className: 'camera-detections-field-col-12',
                templateOptions: {
                  required: true,
                },
              }),
            ],
          },
          {
            templateOptions: {
              label: 'Time Filter',
            },
            wrappers: ['form-field-group'],
            fieldGroupClassName: 'camera-detections-field-row',
            fieldGroup: [
              this._getSmartMotionActivtyMerge({
                className: 'camera-detections-field-col-12',
                defaultValue: 500,
                templateOptions: {
                  required: true,
                },
              }),
              this._getSmartMotionMinActivity({
                className: 'camera-detections-field-col-12',
                defaultValue: 1500,
                templateOptions: {
                  required: true,
                },
              }),
            ],
          },
        ];
      case 'vca_line_crossing_detection':
        return [
          {
            templateOptions: {
              label: 'Triggers',
            },
            wrappers: ['form-field-group'],
            fieldGroupClassName: 'camera-detections-field-row',
            fieldGroup: [
              this._getTriggerName({
                className: 'camera-detections-field-col-12',
                templateOptions: {
                  required: true,
                },
              }),
              this._getLineCrossingTriggerCondition({
                className: 'camera-detections-field-col-12',
                defaultValue: 5,
                templateOptions: {
                  required: true,
                  options: [
                    { label: 'Out -> In', value: 'In' },
                    { label: 'Out <- In', value: 'Out' },
                    { label: 'Out <-> In', value: 'Any' },
                  ],
                },
              }),
            ],
          },
        ];
      case 'vca_intrusion_detection':
        return [
          {
            templateOptions: {
              label: 'Triggers',
            },
            wrappers: ['form-field-group'],
            fieldGroupClassName: 'camera-detections-field-row',
            fieldGroup: [
              this._getTriggerName({
                className: 'camera-detections-field-col-12',
                templateOptions: {
                  required: true,
                },
              }),
              this._getIntrusionTriggerCondition({
                className: 'camera-detections-field-col-12',
                templateOptions: {
                  required: true,
                  options: [
                    { label: 'People Entering Into The Zone', value: 'OutToIn' },
                    { label: 'People Leaving From The Zone', value: 'InToOut' },
                    { label: 'Both Of The Above', value: 'Any' },
                  ],
                },
              }),
            ],
          },
        ];
      default:
        break;
    }
  }

  // Triggers
  private _getTriggerName(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'triggerName',
      type: 'input',
      templateOptions: {
        label: 'Trigger Name',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  private _getLoiteringTriggerCondition(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'loiteringTriggerCondition',
      type: 'input',
      templateOptions: {
        label: 'After A Person Stays In The Zone For More Than ____ Seconds',
        type: 'number',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  private _getCrowdTriggerCondition(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'peopleNumber',
      type: 'input',
      templateOptions: {
        label: 'Number of People In The Zone',
        type: 'number',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  private _getLineCrossingTriggerCondition(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'lineCrossingTriggerCondition',
      type: 'radio',
      templateOptions: {
        label: 'People Walking Direction',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  private _getIntrusionTriggerCondition(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'intrusionTriggerCondition',
      type: 'radio',
      templateOptions: {
        label: 'Condition',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  // Schedules
  private _getSmartMotionDetectionScheduleStart(
    fieldConfig: SnFormFieldConfig = {},
  ): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'smartMotionDetectionStart',
      type: 'timepicker',
      defaultValue: '00:00',
      templateOptions: {
        label: 'From',
        required: true,
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  private _getSmartMotionDetectionScheduleEnd(
    fieldConfig: SnFormFieldConfig = {},
  ): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'smartMotionDetectionEnd',
      type: 'timepicker',
      defaultValue: '00:00',
      templateOptions: {
        label: 'To',
        required: true,
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  // Advance Rules
  private _getEnteredZone(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'enteredZone',
      type: 'input',
      templateOptions: {
        label: 'After a Person Entered The Zone (seconds)',
        type: 'number',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  private _getLeftZone(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'leftZone',
      type: 'input',
      templateOptions: {
        label: 'After a Person Left The Zone (seconds)',
        type: 'number',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  // Smart Motion Rules
  private _getSmartMotionPeopleDetections(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'peopleDetection',
      type: 'checkbox',
      templateOptions: {
        label: 'People Detection',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  private _getSmartMotionVehicleDetections(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'vehicleDetection',
      type: 'checkbox',
      templateOptions: {
        label: 'Vehicle Detection',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  private _getSmartMotionActivity(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'smartMotionActivity',
      type: 'slider',
      defaultValue: 12,
      templateOptions: {
        label: 'Motion Activity',
        min: 1,
        max: 25,
        thumbLabel: true,
        hideFieldUnderline: true,
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  private _getSmartMotionMinActivity(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'minActivityDur',
      type: 'input',
      templateOptions: {
        label: 'Minimum Activity Duration (ms)',
        type: 'number',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }

  private _getSmartMotionActivtyMerge(fieldConfig: SnFormFieldConfig = {}): SnFormFieldConfig {
    const baseFieldConfig = {
      key: 'activityMergeInt',
      type: 'input',
      templateOptions: {
        label: 'Activity Merge Interval (ms)',
        type: 'number',
      },
    };

    return merge(baseFieldConfig, fieldConfig);
  }
}
